import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarIcon, Search, Plus, Minus, Users, Palmtree, X } from "lucide-react";
import { format, parseISO } from "date-fns";
import { es, ptBR, enUS } from "date-fns/locale";
import { cn } from "@/lib/utils";
import type { DateRange } from "react-day-picker";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "react-i18next";
import { useLocale } from "@/contexts/LocaleContext";
import { useNavigate, useSearchParams } from "@/lib/react-router-dom-shim";
import { RESORT_DESTINATIONS, ResortDestination } from "@/types/resort";

interface ResortSearchFormProps {
  defaultRegion?: string;
}

export const ResortSearchForm = ({ defaultRegion }: ResortSearchFormProps = {}) => {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  
  const [selectedDestination, setSelectedDestination] = useState<ResortDestination | null>(null);
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [guestsOpen, setGuestsOpen] = useState(false);
  const [adults, setAdults] = useState(2);
  const [childrenCount, setChildrenCount] = useState(0);
  const [childrenAges, setChildrenAges] = useState<number[]>([]);
  const [showValidation, setShowValidation] = useState(false);

  // Initialize form from URL params or defaultRegion prop
  useEffect(() => {
    const resortDestinationId = searchParams.get("resortDestinationId");
    const checkIn = searchParams.get("checkIn");
    const checkOut = searchParams.get("checkOut");
    const adultsParam = searchParams.get("adults");
    const childrenAgesParam = searchParams.get("childrenAges");

    // Use defaultRegion prop if no URL param, for region-specific pages
    const regionToUse = resortDestinationId || defaultRegion;
    if (regionToUse) {
      const dest = RESORT_DESTINATIONS.find(d => d.id === regionToUse);
      if (dest) setSelectedDestination(dest);
    }

    if (checkIn && checkOut) {
      setDateRange({
        from: parseISO(checkIn),
        to: parseISO(checkOut),
      });
    }

    if (adultsParam) {
      setAdults(parseInt(adultsParam) || 2);
    }

    if (childrenAgesParam && childrenAgesParam.length > 0) {
      const ages = childrenAgesParam.split(",").filter(a => a !== "").map(a => parseInt(a) || 0);
      setChildrenAges(ages);
      setChildrenCount(ages.length);
    }
  }, [searchParams, defaultRegion]);

  const getDateLocale = () => {
    switch (locale) {
      case 'pt-BR':
        return ptBR;
      case 'en':
        return enUS;
      default:
        return es;
    }
  };

  const dateLocale = getDateLocale();

  const handleChildrenCountChange = (newCount: number) => {
    if (newCount < 0) return;
    setChildrenCount(newCount);
    
    if (newCount < childrenAges.length) {
      setChildrenAges(childrenAges.slice(0, newCount));
    } else if (newCount > childrenAges.length) {
      setChildrenAges([...childrenAges, ...Array(newCount - childrenAges.length).fill(0)]);
    }
  };

  const handleChildAgeChange = (index: number, age: number) => {
    const newAges = [...childrenAges];
    newAges[index] = Math.max(0, Math.min(17, age));
    setChildrenAges(newAges);
  };

  const handleSearch = () => {
    if (!selectedDestination || !dateRange?.from || !dateRange?.to) {
      setShowValidation(true);
      return;
    }

    setShowValidation(false);
    const searchParams = new URLSearchParams({
      resortDestinationId: selectedDestination.id,
      resortDestinationName: encodeURIComponent(t(selectedDestination.nameKey) || selectedDestination.name),
      checkIn: format(dateRange.from, "yyyy-MM-dd"),
      checkOut: format(dateRange.to, "yyyy-MM-dd"),
      adults: adults.toString(),
      childrenAges: childrenAges.join(","),
    });

    navigate(`/resorts?${searchParams.toString()}`);
  };

  const isDestinationInvalid = showValidation && !selectedDestination;
  const isDatesInvalid = showValidation && (!dateRange?.from || !dateRange?.to);

  const guestSummary = () => {
    const adultText = adults === 1 
      ? `1 ${t('common.adult')}` 
      : `${adults} ${t('common.adults')}`;
    
    if (childrenCount === 0) {
      return adultText;
    }
    
    const childText = childrenCount === 1 
      ? `1 ${t('common.child')}` 
      : `${childrenCount} ${t('common.children')}`;
    
    return `${adultText}, ${childText}`;
  };

  return (
    <form role="search" aria-label="Buscar resorts" onSubmit={(e) => e.preventDefault()} className="bg-card rounded-xl shadow-2xl p-6 md:p-8">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
        {/* Destination Select */}
        <div className="md:col-span-4">
          <label className={cn(
            "text-xs font-medium mb-1.5 block text-left",
            isDestinationInvalid ? "text-destructive" : "text-muted-foreground"
          )}>
            {t('resorts.selectDestination')} {isDestinationInvalid && "*"}
          </label>
          <Select
            value={selectedDestination?.id || ""}
            onValueChange={(value) => {
              const dest = RESORT_DESTINATIONS.find(d => d.id === value);
              setSelectedDestination(dest || null);
              setShowValidation(false);
            }}
          >
            <SelectTrigger className={cn(
              "w-full h-11 bg-background text-left",
              isDestinationInvalid ? "border-destructive ring-1 ring-destructive" : "border-border"
            )}>
              <div className="flex items-center gap-2 min-w-0">
                <Palmtree className={cn("h-4 w-4 shrink-0", isDestinationInvalid ? "text-destructive" : "text-primary")} />
                <span className="truncate text-sm">
                  <SelectValue placeholder={t('resorts.chooseDestination')} />
                </span>
              </div>
            </SelectTrigger>
            <SelectContent className="bg-popover z-[9999]">
              {RESORT_DESTINATIONS.map((dest) => (
                <SelectItem key={dest.id} value={dest.id} className="cursor-pointer">
                  {t(dest.nameKey) || dest.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Dates */}
        <div className="md:col-span-3">
          <label className={cn(
            "text-xs font-medium mb-1.5 block text-left",
            isDatesInvalid ? "text-destructive" : "text-muted-foreground"
          )}>
            {t('search.dates')} {isDatesInvalid && "*"}
          </label>
          <Popover open={datePickerOpen} onOpenChange={(open) => {
            setDatePickerOpen(open);
            if (open) setShowValidation(false);
          }}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full h-11 justify-start gap-2 bg-background text-left font-normal",
                  isDatesInvalid ? "border-destructive ring-1 ring-destructive" : "border-border",
                  (!dateRange || !dateRange.from) && "text-muted-foreground"
                )}
              >
                <CalendarIcon className={cn("h-4 w-4 shrink-0", isDatesInvalid ? "text-destructive" : "text-primary")} />
                {dateRange && dateRange.from ? (
                  dateRange.to ? (
                    <span className="truncate">
                      {format(dateRange.from, "dd MMM", { locale: dateLocale })} - {format(dateRange.to, "dd MMM yyyy", { locale: dateLocale })}
                    </span>
                  ) : (
                    format(dateRange.from, "dd MMM yyyy", { locale: dateLocale })
                  )
                ) : (
                  <span>{t('search.selectDates')}</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent 
              className="w-auto p-0 bg-popover z-[9999]" 
              align={isMobile ? "center" : "start"}
            >
              <div className="flex flex-col">
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={(range) => {
                    if (range?.from && range?.to && 
                        range.from.getTime() === range.to.getTime()) {
                      return;
                    }
                    setDateRange(range);
                    if (range?.from && range?.to) {
                      setDatePickerOpen(false);
                    }
                  }}
                  disabled={(date) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    
                    if (date < today) {
                      return true;
                    }
                    
                    if (dateRange?.from) {
                      const checkInDate = new Date(dateRange.from);
                      checkInDate.setHours(0, 0, 0, 0);
                      const currentDate = new Date(date);
                      currentDate.setHours(0, 0, 0, 0);
                      
                      if (currentDate.getTime() === checkInDate.getTime()) {
                        return true;
                      }
                    }
                    
                    return false;
                  }}
                  initialFocus
                  numberOfMonths={isMobile ? 1 : 2}
                  locale={dateLocale}
                  className="pointer-events-auto"
                />
                {dateRange?.from && (
                  <div className="border-t p-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full text-muted-foreground hover:text-foreground"
                      onClick={() => setDateRange(undefined)}
                    >
                      <X className="h-4 w-4 mr-2" />
                      {t('common.clearDates')}
                    </Button>
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Guests */}
        <div className="md:col-span-3">
          <label className="text-xs font-medium text-muted-foreground mb-1.5 block text-left">
            {t('search.guests')}
          </label>
          <Popover open={guestsOpen} onOpenChange={setGuestsOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full h-11 justify-start gap-2 bg-background border-border text-left font-normal"
              >
                <Users className="h-4 w-4 text-primary shrink-0" />
                <span className="truncate text-sm">{guestSummary()}</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-4 bg-popover z-[9999]" align="start">
              <div className="space-y-4">
                {/* Adults */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{t('search.adults')}</p>
                    <p className="text-sm text-muted-foreground">18+</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setAdults(Math.max(1, adults - 1))}
                      disabled={adults <= 1}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="w-8 text-center font-medium">{adults}</span>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setAdults(adults + 1)}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Children */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{t('search.children')}</p>
                    <p className="text-sm text-muted-foreground">0-17</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleChildrenCountChange(childrenCount - 1)}
                      disabled={childrenCount <= 0}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="w-8 text-center font-medium">{childrenCount}</span>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => handleChildrenCountChange(childrenCount + 1)}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Children Ages */}
                {childrenCount > 0 && (
                  <div className="space-y-2 pt-2 border-t">
                    <p className="text-sm text-muted-foreground">{t('search.childrenAge')}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {childrenAges.map((age, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">{index + 1}.</span>
                          <Select
                            value={age.toString()}
                            onValueChange={(value) => handleChildAgeChange(index, parseInt(value))}
                          >
                            <SelectTrigger className="h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-popover z-[9999]">
                              {Array.from({ length: 18 }, (_, i) => (
                                <SelectItem key={i} value={i.toString()}>
                                  {i} {i === 1 ? t('search.year') : t('search.years')}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Search Button */}
        <div className="md:col-span-2">
          <Button 
            onClick={handleSearch}
            className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold gap-2"
          >
            <Search className="h-4 w-4" />
            <span className="hidden sm:inline">{t('common.search')}</span>
          </Button>
        </div>
      </div>
    </form>
  );
};
